/**
 * / — landing for authenticated users. Identity card + storage stats +
 * quick links + recent audit. Visual follows the Lovable design brief.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import { Badge, Icon, Pill } from '../components/brand';

interface AccountSummary {
  id: string;
  name: string;
  provider: string;
}

interface AuditRow {
  id: number;
  userId: string | null;
  action: string;
  accountId: string | null;
  bucket: string | null;
  objectKey: string | null;
  status: 'ok' | 'denied' | 'error';
  createdAt: number;
}

interface AuditPage {
  items: AuditRow[];
  cursor: string | null;
}

export function Dashboard() {
  const { user, claims, isAdmin } = useAuth();
  const navigate = useNavigate();

  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<AccountSummary[]>('/api/accounts'),
  });

  const auditQ = useQuery({
    queryKey: ['audit', 'recent'],
    queryFn: () => api.get<AuditPage>('/api/audit?limit=8'),
    enabled: !!user,
  });

  const accountsCount = accountsQ.data?.length ?? 0;
  const repos = claims?.repos ?? [];
  const recent = auditQ.data?.items ?? [];

  return (
    <div data-screen-label="02 Dashboard">
      <div style={{ marginBottom: 28 }}>
        <h1 className="h-page">Dashboard</h1>
        <p className="muted" style={{ marginTop: 6 }}>
          What's hot, who you are, where to go next.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        <div className="bm-card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="bm-row bm-gap-12">
            <span className="h-eyebrow">Identity</span>
            {claims?.writable && <Badge variant="success">writable</Badge>}
            {isAdmin && <Badge variant="accent">admin</Badge>}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              rowGap: 8,
              columnGap: 16,
            }}
          >
            <span className="muted" style={{ fontSize: 12 }}>
              email
            </span>
            <span className="mono" style={{ fontSize: 12.5 }}>
              {user?.email ?? '—'}
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              sub
            </span>
            <span className="mono" style={{ fontSize: 12.5, color: 'var(--bm-text-soft)' }}>
              {user?.sub ?? '—'}
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              role
            </span>
            <span className="mono" style={{ fontSize: 12.5 }}>
              {claims?.role ?? '—'}
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              repos
            </span>
            <div className="bm-row bm-gap-4" style={{ flexWrap: 'wrap' }}>
              {repos.length === 0 ? (
                <span className="muted" style={{ fontSize: 12 }}>
                  —
                </span>
              ) : (
                repos.map((r) => (
                  <Pill key={r} style={{ fontSize: 11 }}>
                    <Icon.bucket size={10} />
                    {r}
                  </Pill>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="bm-card" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <span className="h-eyebrow">Storage at a glance</span>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              rowGap: 18,
              columnGap: 24,
            }}
          >
            <Stat label="accounts" value={accountsQ.isLoading ? '…' : String(accountsCount)} />
            <Stat label="repos" value={String(repos.length)} />
          </div>
        </div>

        <div
          className="bm-card"
          style={{
            gridColumn: '1 / -1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 16,
          }}
        >
          <span className="h-eyebrow" style={{ paddingLeft: 8 }}>
            Quick links
          </span>
          <div className="bm-row bm-gap-8">
            <button
              type="button"
              className="bm-btn bm-btn-ghost bm-btn-pill bm-btn-sm"
              onClick={() => navigate('/accounts')}
            >
              <Icon.plus size={12} /> Add an account
            </button>
            <button
              type="button"
              className="bm-btn bm-btn-ghost bm-btn-pill bm-btn-sm"
              onClick={() => navigate('/accounts')}
            >
              <Icon.bucket size={12} /> Open a bucket
            </button>
            {isAdmin && (
              <button
                type="button"
                className="bm-btn bm-btn-ghost bm-btn-pill bm-btn-sm"
                onClick={() => navigate('/admin/audit')}
              >
                <Icon.audit size={12} /> View audit
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                className="bm-btn bm-btn-ghost bm-btn-pill bm-btn-sm"
                onClick={() => navigate('/admin/api-keys')}
              >
                <Icon.key size={12} /> Issue API key
              </button>
            )}
          </div>
        </div>

        <div className="bm-card-flush" style={{ gridColumn: '1 / -1' }}>
          <div className="bm-card-header">
            <div className="bm-row bm-gap-8">
              <span className="h-eyebrow">Your recent activity</span>
              <span className="muted" style={{ fontSize: 12 }}>
                · last 24h
              </span>
            </div>
            {isAdmin && (
              <button
                type="button"
                className="bm-btn bm-btn-sm"
                onClick={() => navigate('/admin/audit')}
                style={{ color: 'var(--bm-text-muted)' }}
              >
                View all <Icon.chevronRight size={12} />
              </button>
            )}
          </div>
          <table className="bm-table">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Time</th>
                <th style={{ width: 160 }}>Action</th>
                <th style={{ width: 90 }}>Status</th>
                <th>Target</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={4} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    {auditQ.isLoading ? 'Loading…' : 'No activity yet.'}
                  </td>
                </tr>
              ) : (
                recent.map((r) => (
                  <tr key={r.id}>
                    <td
                      className="mono tnum"
                      style={{ color: 'var(--bm-text-muted)', fontSize: 12 }}
                    >
                      {formatTs(r.createdAt)}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {r.action}
                    </td>
                    <td>
                      {r.status === 'ok' && <Badge variant="success">ok</Badge>}
                      {r.status === 'denied' && <Badge variant="warn">denied</Badge>}
                      {r.status === 'error' && <Badge variant="danger">error</Badge>}
                    </td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--bm-text-soft)' }}>
                      {renderTarget(r) || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="bm-col bm-gap-4">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: '-0.6px',
            fontFamily: 'var(--bm-font)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </span>
        {suffix && (
          <span className="muted mono" style={{ fontSize: 11 }}>
            {suffix}
          </span>
        )}
      </div>
      <span className="h-eyebrow" style={{ fontSize: 10 }}>
        {label}
      </span>
    </div>
  );
}

function formatTs(ts: number | string): string {
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toISOString().slice(11, 19);
}

function renderTarget(r: AuditRow): string {
  const parts: string[] = [];
  if (r.accountId) parts.push(r.accountId);
  if (r.bucket) parts.push(r.bucket);
  if (r.objectKey) parts.push(r.objectKey);
  return parts.join(' / ');
}
